import React, {useCallback, useEffect, useRef, useState} from 'react';
import {runInAction} from 'mobx';
import {observer} from 'mobx-react';
import s from '../../s';
import ModalSimple from '../../../global/modal-simple';
import {Swiper, SwiperSlide} from 'swiper/react';
import 'swiper/css';
import 'swiper/css/scrollbar';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/zoom';
import {Keyboard, Scrollbar, Navigation, Zoom} from 'swiper/modules';
import {r} from '../../rendering/core';
import {prerender_set_pcs} from '../../rendering/web-only-render';
import * as emitter from '../../../global/emitter';
import {screenResizeStore} from '../../../global/store/screen-resize-store';
import {ProductCustomizationState} from '../../rendering/render.def';
import {ariaPulse, modal, Portal, RenderType} from '../../../util';
import {
    FRAMERLY_THUMB_SWIPER_BREAKPOINTS,
    buildFramerlyThumbSlides,
    clearFramerlyScheduledTimeouts,
    findFramerlySlideIndexByKey,
    framerlyThumbSrc,
    framerlyThumbsFromProduct,
    getFramerlySlidesPerView,
    scheduleFramerlyZoomNavigation,
    shouldEnableFramerlyThumbKeyboard,
    shouldLoopFramerlyThumbs,
    shouldLoopFramerlyZoom,
    shouldShowFramerlyThumbNavigation,
    type FramerlyThumbSlide,
} from './framerly-thumbs/framerly-thumbs';

declare const window: any;

const THUMB_SWIPER_ID = 'prod-swiper-th';
const ZOOM_SWIPER_ID = 'prod-swiper-th-zoom';
const ZOOM_MODAL_ID = 'modal-prod-swiper-th';

const SliderTH: React.FC = observer(() => {
    const [selectedKey, setSelectedKey] = useState('');
    const zoomTimersRef = useRef<Array<ReturnType<typeof setTimeout>>>([]);
    const openerElRef = useRef<HTMLElement | null>(null);
    const focusTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const selectedKeyRef = useRef(selectedKey);
    selectedKeyRef.current = selectedKey;

    const ready = s.ready;
    const prod = s.prod;
    const slides: FramerlyThumbSlide[] = !ready || !prod ? [] : buildFramerlyThumbSlides({ready: true, customizationMethod: prod.CustomizationMethod, thumbs: framerlyThumbsFromProduct(prod)});
    const slidesRef = useRef(slides);
    slidesRef.current = slides;

    const slidesPerView = getFramerlySlidesPerView(screenResizeStore.width);
    const doLoop = shouldLoopFramerlyThumbs(slides.length, slidesPerView);
    const showNavigation = shouldShowFramerlyThumbNavigation(slides.length, slidesPerView);
    const keyboardEnabled = shouldEnableFramerlyThumbKeyboard(slides.length, slidesPerView);
    const zoomLoop = shouldLoopFramerlyZoom(slides.length);

    const clearZoomTimers = useCallback(() => {
        clearFramerlyScheduledTimeouts(zoomTimersRef.current);
        zoomTimersRef.current = [];
    }, []);

    const clearFocusTimer = useCallback(() => {
        if (focusTimerRef.current != null) {
            clearTimeout(focusTimerRef.current);
            focusTimerRef.current = null;
        }
    }, []);

    const restoreOpenerFocus = useCallback(() => {
        const opener = openerElRef.current;
        openerElRef.current = null;
        if (!opener || typeof document === 'undefined' || !document.body.contains(opener)) {
            return;
        }
        clearFocusTimer();
        focusTimerRef.current = setTimeout(() => {
            if (document.body.contains(opener)) {
                opener.focus({preventScroll: true});
            }
        }, 0);
    }, [clearFocusTimer]);

    const closeZoom = useCallback(() => {
        clearZoomTimers();
        runInAction(() => {
            s.ui.zoom_thumbnails = false;
        });
        setSelectedKey('');
        restoreOpenerFocus();
    }, [clearZoomTimers, restoreOpenerFocus]);

    const closeZoomRef = useRef(closeZoom);
    closeZoomRef.current = closeZoom;

    const loadSwiper = useCallback(async () => {
        ariaPulse();
    }, []);

    const loadSwiperZoom = useCallback(
        async (swiper: any) => {
            clearZoomTimers();
            const index = findFramerlySlideIndexByKey(slidesRef.current, selectedKeyRef.current);
            zoomTimersRef.current = scheduleFramerlyZoomNavigation({swiper, index});
            ariaPulse();
        },
        [clearZoomTimers],
    );

    const openZoom = useCallback((key: string, opener: HTMLElement | null) => {
        openerElRef.current = opener;
        selectedKeyRef.current = key;
        setSelectedKey(key);
        prerender_set_pcs();
        let clone: ProductCustomizationState;
        try {
            clone = structuredClone(s.pcs);
        } catch {
            clone = JSON.parse(JSON.stringify(s.pcs));
        }
        runInAction(() => {
            s.ps.css_zoom = r.render(clone, RenderType.Zoom);
            s.ui.zoom_thumbnails = true;
        });
        modal(ZOOM_MODAL_ID);
    }, []);

    useEffect(() => {
        // Slide descriptors are derived from product observables. Keep this
        // channel subscribed for the shared emitter contract without a
        // forceUpdate, so init/ready cannot resubscribe the AB channel.
        emitter.eventEmitter.subscribe(emitter.EventType.AB, 'ab', () => undefined);

        const handleModalClosed = (e: Event) => {
            if ((e as CustomEvent).detail === ZOOM_MODAL_ID) {
                closeZoomRef.current();
            }
        };
        document.addEventListener('modal-closed', handleModalClosed);

        return () => {
            emitter.eventEmitter.unsubscribe(emitter.EventType.AB, 'ab');
            document.removeEventListener('modal-closed', handleModalClosed);
            clearFramerlyScheduledTimeouts(zoomTimersRef.current);
            zoomTimersRef.current = [];
            if (focusTimerRef.current != null) {
                clearTimeout(focusTimerRef.current);
                focusTimerRef.current = null;
            }
            const opener = openerElRef.current;
            openerElRef.current = null;
            if (opener && typeof document !== 'undefined' && document.body.contains(opener)) {
                opener.focus({preventScroll: true});
            }
            runInAction(() => {
                s.ui.zoom_thumbnails = false;
            });
        };
    }, []);

    useEffect(() => {
        if (slides.length > 0) {
            return;
        }
        clearZoomTimers();
        if (s.ui.zoom_thumbnails) {
            runInAction(() => {
                s.ui.zoom_thumbnails = false;
            });
            restoreOpenerFocus();
        }
        setSelectedKey(current => (current ? '' : current));
    }, [slides.length, clearZoomTimers, restoreOpenerFocus]);

    if (!ready) {
        return null;
    }
    if (!prod || slides.length < 1) {
        return null;
    }

    const cdn = Portal?.CDNDomain;

    return (
        <div>
            <Swiper
                id={THUMB_SWIPER_ID}
                key={`prod-swiper-th-loop-${doLoop}`}
                onInit={loadSwiper}
                slidesPerView={3}
                breakpoints={FRAMERLY_THUMB_SWIPER_BREAKPOINTS}
                centeredSlidesBounds={!doLoop}
                centeredSlides={!doLoop}
                centerInsufficientSlides={!doLoop}
                grabCursor={doLoop}
                zoom={false}
                keyboard={{
                    enabled: keyboardEnabled,
                }}
                scrollbar={false}
                navigation={showNavigation}
                height={75}
                loop={doLoop}
                pagination={false}
                modules={[Keyboard, Scrollbar, Navigation, Zoom]}
                aria-roledescription="carousel"
                role="region"
                aria-label="Product images"
            >
                {slides.map(slide => (
                    <SwiperSlide key={slide.key}>
                        <button
                            type="button"
                            className="prod-swiper-th-thumb"
                            aria-label={slide.thumbAriaLabel}
                            aria-haspopup="dialog"
                            onClick={e => openZoom(slide.key, e.currentTarget)}
                        >
                            <picture>
                                <source
                                    type="image/webp"
                                    srcSet={framerlyThumbSrc(cdn, slide.url)}
                                />
                                <img
                                    alt={slide.accessibleName}
                                    src={framerlyThumbSrc(cdn, slide.url)}
                                    height={75}
                                    width="75"
                                />
                            </picture>
                        </button>
                    </SwiperSlide>
                ))}
            </Swiper>
            {s.ui.zoom_thumbnails && (
                <ModalSimple
                    id={ZOOM_MODAL_ID}
                    title="Product Zoom"
                    open={true}
                    maxWidth={'90%'}
                    close={closeZoom}
                >
                    <Swiper
                        id={ZOOM_SWIPER_ID}
                        onInit={loadSwiperZoom}
                        slidesPerView={1}
                        centeredSlides={true}
                        grabCursor={true}
                        zoom={true}
                        keyboard={{enabled: zoomLoop}}
                        loop={zoomLoop}
                        scrollbar={true}
                        navigation={zoomLoop}
                        pagination={false}
                        modules={[Keyboard, Scrollbar, Navigation, Zoom]}
                        slidesPerGroupAuto={true}
                        aria-roledescription="carousel"
                        role="region"
                        aria-label="Product images"
                    >
                        {slides.map(slide => (
                            <SwiperSlide
                                key={slide.key}
                                id={`slide-prod-real-${slide.key}`}
                            >
                                <picture className="swiper-zoom-container">
                                    <source
                                        type="image/webp"
                                        srcSet={framerlyThumbSrc(cdn, slide.url)}
                                    />
                                    <img
                                        src={framerlyThumbSrc(cdn, slide.url)}
                                        alt={slide.zoomAccessibleName}
                                        style={{
                                            maxWidth: window.innerWidth * 0.8,
                                            maxHeight: window.innerHeight * 0.8,
                                        }}
                                    />
                                </picture>
                            </SwiperSlide>
                        ))}
                    </Swiper>
                </ModalSimple>
            )}
        </div>
    );
});

export default SliderTH;
